import { useAtom } from 'jotai';
import { userAtom, accessTokenAtom } from '../../../atoms';
import { Navigate } from 'react-router-dom';
import { paths } from '../../../../config/paths';
import { useCallback, useEffect, useState } from 'react';

interface CardRequest {
    user: {
        id: string;
        email: string;
        fujiyama_telegram_id: string | null;
        fujiyama_referral_code: string | null;
    };
    activity: {
        id: string;
        status: string;
        wallet_chain: string | null;
        wallet_address: string | null;
        wallet_transaction_id: string | null;
        debit_card_category: string | null;
        amount: number;
    };
}

interface CardRequestsResponse {
    requests: CardRequest[];
    total: number;
    page: number;
    hasMore: boolean;
}

const useCardRequests = (page: number) => {
    const [user] = useAtom(userAtom);
    const [accessToken] = useAtom(accessTokenAtom);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [data, setData] = useState<CardRequestsResponse | null>(null);
    const [processingIds, setProcessingIds] = useState<Set<string>>(new Set());

    const fetchRequests = useCallback(async () => {
        if (!user || !accessToken) {
            setError('認証情報が見つかりません');
            return;
        }

        setIsLoading(true);
        setError(null);

        try {
            const response = await fetch(`/api/v1/admin/debit-card-request?page=${page}`, {
                headers: {
                    'Authorization': `Bearer ${accessToken}`,
                },
            });

            if (!response.ok) {
                throw new Error('リクエストの取得に失敗しました');
            }

            const responseData: CardRequestsResponse = await response.json();
            setData(responseData);
        } catch (err) {
            setError(err instanceof Error ? err.message : '予期せぬエラーが発生しました');
        } finally {
            setIsLoading(false);
        }
    }, [user, accessToken, page]);

    const approveRequest = useCallback(async (activityId: string) => {
        if (!user || !accessToken) return;

        setProcessingIds(prev => new Set(prev).add(activityId));
        try {
            const response = await fetch('/api/v1/admin/debit-card-request', {
                method: 'PUT',
                headers: {
                    'Authorization': `Bearer ${accessToken}`,
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    requests: [{
                        id: activityId,
                        status: 'processed'
                    }]
                })
            });

            if (!response.ok) {
                throw new Error('承認処理に失敗しました');
            }

            // ローカルでデータを更新
            setData(prev => {
                if (!prev) return prev;
                return {
                    ...prev,
                    requests: prev.requests.map(request =>
                        request.activity.id === activityId
                            ? {
                                ...request,
                                activity: {
                                    ...request.activity,
                                    status: 'processed'
                                }
                            }
                            : request
                    )
                };
            });
        } catch (err) {
            setError(err instanceof Error ? err.message : '予期せぬエラーが発生しました');
        } finally {
            setProcessingIds(prev => {
                const next = new Set(prev);
                next.delete(activityId);
                return next;
            });
        }
    }, [user, accessToken]);

    const rejectRequest = useCallback(async (activityId: string) => {
        if (!user || !accessToken) return;

        setProcessingIds(prev => new Set(prev).add(activityId));
        try {
            const response = await fetch(`/api/v1/admin/debit-card-request/${activityId}`, {
                method: 'DELETE',
                headers: {
                    'Authorization': `Bearer ${accessToken}`,
                }
            });

            if (!response.ok) {
                throw new Error('否認処理に失敗しました');
            }

            // ローカルでデータを更新
            setData(prev => {
                if (!prev) return prev;
                return {
                    ...prev,
                    requests: prev.requests.filter(request => request.activity.id !== activityId)
                };
            });
        } catch (err) {
            setError(err instanceof Error ? err.message : '予期せぬエラーが発生しました');
        } finally {
            setProcessingIds(prev => {
                const next = new Set(prev);
                next.delete(activityId);
                return next;
            });
        }
    }, [user, accessToken]);

    useEffect(() => {
        fetchRequests();
    }, [fetchRequests]);

    return {
        data,
        isLoading,
        error,
        refetch: fetchRequests,
        approveRequest,
        rejectRequest,
        processingIds
    };
};

export const CardRequestsPage = () => {
    const [user] = useAtom(userAtom);
    const [currentPage, setCurrentPage] = useState(1);
    const {
        data,
        isLoading,
        error,
        approveRequest,
        rejectRequest,
        processingIds
    } = useCardRequests(currentPage);

    if (!user?.has_admin_role) {
        return <Navigate to={paths.dashboard.path} replace />;
    }

    const handlePreviousPage = () => {
        if (currentPage > 1) {
            setCurrentPage(prev => prev - 1);
        }
    };

    const handleNextPage = () => {
        if (data?.hasMore) {
            setCurrentPage(prev => prev + 1);
        }
    };

    return (
        <div className="max-w-6xl mx-auto">
            <h1 className="text-2xl font-bold mb-6">FujiyamaTap Debit購入確認リクエスト</h1>
            <div className="bg-white shadow rounded-lg p-6">
                <div className="space-y-4">
                    {error && (
                        <div className="bg-red-50 text-red-700 p-4 rounded-lg mb-4">
                            {error}
                        </div>
                    )}

                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        ユーザー情報
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        ステータス
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        ウォレット情報
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        カード種別
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        価格
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        アクション
                                    </th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                {isLoading ? (
                                    <tr>
                                        <td colSpan={6} className="px-6 py-4 text-center">
                                            <div className="flex justify-center items-center">
                                                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
                                            </div>
                                        </td>
                                    </tr>
                                ) : data?.requests.length === 0 ? (
                                    <tr>
                                        <td colSpan={6} className="px-6 py-4 text-center text-gray-500">
                                            リクエストが見つかりませんでした
                                        </td>
                                    </tr>
                                ) : (
                                    data?.requests.map((request) => (
                                        <tr key={request.activity.id}>
                                            <td className="px-6 py-4">
                                                <div className="text-sm">
                                                    <div className="font-medium text-gray-900">{request.user.email}</div>
                                                    {request.user.fujiyama_telegram_id && (
                                                        <div className="text-gray-500">
                                                            Telegram ID: {request.user.fujiyama_telegram_id}
                                                        </div>
                                                    )}
                                                    {request.user.fujiyama_referral_code && (
                                                        <div className="text-gray-500">
                                                            Fujiyama Referral Code: {request.user.fujiyama_referral_code}
                                                        </div>
                                                    )}
                                                </div>
                                            </td>
                                            <td className="px-6 py-4">
                                                <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${request.activity.status === 'pending'
                                                    ? 'bg-yellow-100 text-yellow-800'
                                                    : request.activity.status === 'processed'
                                                        ? 'bg-blue-100 text-blue-800'
                                                        : request.activity.status === 'rejected'
                                                            ? 'bg-red-100 text-red-800'
                                                            : 'bg-gray-100 text-gray-800'
                                                    }`}>
                                                    {request.activity.status}
                                                </span>
                                            </td>
                                            <td className="px-6 py-4">
                                                <div className="text-sm text-gray-900">
                                                    {request.activity.wallet_chain && (
                                                        <div>Chain: {request.activity.wallet_chain}</div>
                                                    )}
                                                    {request.activity.wallet_address && (
                                                        <div className="truncate max-w-xs">
                                                            送金先: {request.activity.wallet_address}
                                                        </div>
                                                    )}
                                                    {request.activity.wallet_transaction_id && (
                                                        <div className="truncate max-w-xs">
                                                            TXID: {request.activity.wallet_transaction_id}
                                                        </div>
                                                    )}
                                                </div>
                                            </td>
                                            <td className="px-6 py-4">
                                                {request.activity.debit_card_category}
                                            </td>
                                            <td className="px-6 py-4">
                                                {request.activity.amount} USDT
                                            </td>
                                            <td className="px-6 py-4 text-sm">
                                                <div className="flex space-x-2">
                                                    {request.activity.status === 'checking' && (
                                                        <>
                                                            <button
                                                                onClick={() => approveRequest(request.activity.id)}
                                                                disabled={processingIds.has(request.activity.id)}
                                                                className={`px-4 py-2 rounded-md ${processingIds.has(request.activity.id)
                                                                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                                                                    : 'bg-blue-500 text-white hover:bg-blue-600'
                                                                    }`}
                                                            >
                                                                {processingIds.has(request.activity.id) ? (
                                                                    <span className="inline-block animate-spin">⌛</span>
                                                                ) : (
                                                                    '承認'
                                                                )}
                                                            </button>
                                                            <button
                                                                onClick={() => rejectRequest(request.activity.id)}
                                                                disabled={processingIds.has(request.activity.id)}
                                                                className={`px-4 py-2 rounded-md ${processingIds.has(request.activity.id)
                                                                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                                                                    : 'bg-red-500 text-white hover:bg-red-600'
                                                                    }`}
                                                            >
                                                                {processingIds.has(request.activity.id) ? (
                                                                    <span className="inline-block animate-spin">⌛</span>
                                                                ) : (
                                                                    '否認'
                                                                )}
                                                            </button>
                                                        </>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>

                    {/* ページネーション */}
                    {data && (
                        <div className="flex items-center justify-between mt-4">
                            <div className="flex items-center">
                                <span className="text-sm text-gray-700">
                                    全{data.total}件中 {(currentPage - 1) * 50 + 1}-
                                    {Math.min(currentPage * 50, data.total)}件を表示
                                </span>
                            </div>
                            <div className="flex items-center space-x-2">
                                <button
                                    onClick={handlePreviousPage}
                                    disabled={currentPage === 1}
                                    className={`px-3 py-1 border rounded-md ${currentPage === 1
                                        ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                                        : 'bg-white text-gray-700 hover:bg-gray-50'
                                        }`}
                                >
                                    前へ
                                </button>
                                <button
                                    onClick={handleNextPage}
                                    disabled={!data.hasMore}
                                    className={`px-3 py-1 border rounded-md ${!data.hasMore
                                        ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                                        : 'bg-white text-gray-700 hover:bg-gray-50'
                                        }`}
                                >
                                    次へ
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default CardRequestsPage;